import { db } from '@/lib/firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  query,
  orderBy,
  limit as firestoreLimit
} from 'firebase/firestore';
import type { TreatmentSupportRequest } from '@/types';

const requestsCollection = collection(db, 'treatmentSupportRequests');

// Helper to convert Firestore Timestamp to Date
const toDate = (value: any): Date => {
  if (!value) return new Date();
  if (value.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value);
};

// Helper to safely convert a value to a valid Date, returning null for invalid/empty values
const toValidDate = (value: any): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

// Helper to convert a valid Date to a Firestore Timestamp, or return null
const toTimestampOrNull = (value: any): ReturnType<typeof Timestamp.fromDate> | null => {
  const date = toValidDate(value);
  return date ? Timestamp.fromDate(date) : null;
};

// Recursively remove undefined values from an object (Firebase rejects undefined at any depth)
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefined);
  if (typeof obj === 'object' && !(obj instanceof Date) && !(obj.toDate)) {
    const cleaned: any = {};
    for (const key of Object.keys(obj)) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
};

export async function getTreatmentSupportRequests(): Promise<TreatmentSupportRequest[]> {
  const snapshot = await getDocs(requestsCollection);

  const results = snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    // Convert medical reports dates
    const medicalReports = data.medicalReports?.map((report: any) => ({
      ...report,
      uploadedAt: report.uploadedAt ? toDate(report.uploadedAt) : undefined,
    })) || [];

    // Convert progress reports dates
    const progressReports = data.progressReports?.map((report: any) => ({
      ...report,
      uploadedAt: report.uploadedAt ? toDate(report.uploadedAt) : undefined,
      reportDate: report.reportDate ? toDate(report.reportDate) : undefined,
    })) || [];

    return {
      id: docSnap.id,
      caseNumber: data.caseNumber,
      userId: data.userId || '',
      
      // New applicant fields
      nameArabic: data.nameArabic || data.applicantName || '',
      nameEnglish: data.nameEnglish || '',
      idNumber: data.idNumber || data.nationalId || '',
      idType: data.idType || 'national_id',
      dateOfBirth: data.dateOfBirth ? toDate(data.dateOfBirth) : new Date(),
      idExpiryDate: data.idExpiryDate ? toDate(data.idExpiryDate) : new Date(),
      nationality: data.nationality || '',
      gender: data.gender || 'male',
      maritalStatus: data.maritalStatus || 'single',
      familyMembersCount: data.familyMembersCount || 0,
      email: data.email || '',
      educationLevel: data.educationLevel || undefined,
      workSector: data.workSector || undefined,
      profession: data.profession || '',
      monthlyIncome: data.monthlyIncome ?? undefined,
      housingType: data.housingType || undefined,
      rentAmount: data.rentAmount ?? undefined,
      city: data.city || '',
      neighborhood: data.neighborhood || '',
      phoneNumber: data.phoneNumber || '',
      relativeMobileNumber: data.relativeMobileNumber || '',
      
      // Case status fields
      isCritical: data.isCritical ?? false,
      isHospitalized: data.isHospitalized ?? false,
      hasInsurance: data.hasInsurance ?? false,
      insuranceProvider: data.insuranceProvider || '',
      insuranceCategory: data.insuranceCategory || '',
      
      // Legacy fields (backward compatibility)
      nationalId: data.nationalId || data.idNumber || '',
      applicantName: data.applicantName || data.nameArabic || '',
      age: data.age || 0,
      
      // Medical information
      medicalCondition: data.medicalCondition || data.details?.split('\n')[0] || '',
      requiredTreatment: data.requiredTreatment || data.details?.split('\n')[1] || '',
      cost: data.cost || 0,
      medicalReportUrl: data.medicalReportUrl || '',
      medicalReports,
      progressReports,
      treatmentStartedAt: data.treatmentStartedAt ? toDate(data.treatmentStartedAt) : undefined,
      treatmentStartedBy: data.treatmentStartedBy,
      status: data.status || 'pending',
      
      // Coordinator review
      coordinatorId: data.coordinatorId,
      coordinatorNotes: data.coordinatorNotes || data.providerNotes || '',
      socialStatusFits: data.socialStatusFits,
      medicalConditionFits: data.medicalConditionFits,
      reviewedAt: data.reviewedAt ? toDate(data.reviewedAt) : undefined,
      
      // Admin decision
      adminId: data.adminId,
      adminNotes: data.adminNotes,
      adminDecisionAt: data.adminDecisionAt ? toDate(data.adminDecisionAt) : undefined,
      approvedAmount: data.approvedAmount,
      
      // Invoices & Payment
      invoiceUrls: data.invoiceUrls || [],
      invoices: data.invoices?.map((inv: any) => ({
        url: inv.url,
        name: inv.name,
        uploadedAt: inv.uploadedAt ? toDate(inv.uploadedAt) : undefined,
        uploadedBy: inv.uploadedBy,
        providerId: inv.providerId,
        providerName: inv.providerName,
        providerNameAr: inv.providerNameAr,
        vatNumber: inv.vatNumber,
        invoiceDate: inv.invoiceDate ? toDate(inv.invoiceDate) : undefined,
        invoiceNumber: inv.invoiceNumber,
        totalBeforeDiscount: inv.totalBeforeDiscount,
        totalAfterDiscount: inv.totalAfterDiscount,
        taxableAmount: inv.taxableAmount,
        nonTaxableAmount: inv.nonTaxableAmount,
        taxAmount: inv.taxAmount,
      })) || [],
      invoicesUploadedAt: data.invoicesUploadedAt ? toDate(data.invoicesUploadedAt) : undefined,
      invoicesUploadedBy: data.invoicesUploadedBy,
      paymentSlipUrls: data.paymentSlipUrls || [],
      paymentSlipsUploadedAt: data.paymentSlipsUploadedAt ? toDate(data.paymentSlipsUploadedAt) : undefined,
      paymentSlipsUploadedBy: data.paymentSlipsUploadedBy,
      
      // Donations
      donationEntries: data.donationEntries?.map((entry: { linkId?: string; platform: string; caseNumber: string; platformCost?: number; amount?: number; addedAt?: unknown; addedBy?: string }) => ({
        linkId: entry.linkId || '',
        platform: entry.platform,
        caseNumber: entry.caseNumber,
        amount: entry.amount ?? entry.platformCost,
        addedAt: entry.addedAt ? toDate(entry.addedAt) : undefined,
        addedBy: entry.addedBy,
      })) || (data.donationPlatform ? [{ linkId: '', platform: data.donationPlatform, caseNumber: data.donationCaseNumber || '' }] : []),
      
      // ID Document
      idDocumentUrl: data.idDocumentUrl || '',
      idDocumentName: data.idDocumentName || '',
      idDocumentUploadedAt: data.idDocumentUploadedAt ? toDate(data.idDocumentUploadedAt) : undefined,
      
      // Case Updates
      updates: data.updates?.map((update: { id: string; content: string; statusAtTime: string; createdAt: unknown; createdBy: string; createdByName: string; createdByRole: string }) => ({
        id: update.id,
        content: update.content,
        statusAtTime: update.statusAtTime,
        createdAt: update.createdAt ? toDate(update.createdAt) : new Date(),
        createdBy: update.createdBy,
        createdByName: update.createdByName,
        createdByRole: update.createdByRole,
      })) || [],
      
      // Category / Classification
      categoryId: data.categoryId || '',
      categoryName: data.categoryName || '',
      
      // Metadata
      createdAt: toDate(data.createdAt),
      updatedAt: data.updatedAt ? toDate(data.updatedAt) : undefined,
      createdBy: data.createdBy,
      submittedByName: data.submittedByName,
      ...(data.isLinkHolder ? { isLinkHolder: true } : {}),
    } as TreatmentSupportRequest;
  });

  // Sort client-side (descending by createdAt) so docs without createdAt are still included
  return results.sort((a, b) => {
    const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
    const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
    return bTime - aTime;
  });
}

async function getNextCaseNumber(): Promise<number> {
  const q = query(requestsCollection, orderBy('caseNumber', 'desc'), firestoreLimit(1));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return 1;
  const maxCaseNumber = snapshot.docs[0].data().caseNumber;
  return (typeof maxCaseNumber === 'number' ? maxCaseNumber : 0) + 1;
}

export async function addTreatmentSupportRequest(request: Omit<TreatmentSupportRequest, 'id'>): Promise<number> {
  const caseNumber = await getNextCaseNumber();
  const docData: any = {
    ...request,
    caseNumber,
    createdAt: toTimestampOrNull(request.createdAt) ?? Timestamp.fromDate(new Date()),
  };
  
  // Convert applicant date fields (safely handle invalid/empty dates)
  docData.dateOfBirth = toTimestampOrNull(request.dateOfBirth);
  docData.idExpiryDate = toTimestampOrNull(request.idExpiryDate);
  
  // Convert other date fields if present
  docData.updatedAt = toTimestampOrNull(request.updatedAt);
  docData.reviewedAt = toTimestampOrNull(request.reviewedAt);
  docData.adminDecisionAt = toTimestampOrNull(request.adminDecisionAt);
  docData.invoicesUploadedAt = toTimestampOrNull(request.invoicesUploadedAt);
  docData.paymentSlipsUploadedAt = toTimestampOrNull(request.paymentSlipsUploadedAt);
  docData.idDocumentUploadedAt = toTimestampOrNull(request.idDocumentUploadedAt);
  docData.treatmentStartedAt = toTimestampOrNull(request.treatmentStartedAt);
  
  // Convert medical reports dates
  if (request.medicalReports) {
    docData.medicalReports = request.medicalReports.map(report => ({
      ...report,
      uploadedAt: toTimestampOrNull(report.uploadedAt),
      reportDate: toTimestampOrNull((report as any).reportDate),
    }));
  }

  // Convert progress reports dates
  if (request.progressReports) {
    docData.progressReports = request.progressReports.map(report => ({
      ...report,
      uploadedAt: toTimestampOrNull(report.uploadedAt),
      reportDate: toTimestampOrNull(report.reportDate),
    }));
  }

  // Convert invoices dates
  if (request.invoices) {
    docData.invoices = request.invoices.map(invoice => ({
      ...invoice,
      uploadedAt: toTimestampOrNull(invoice.uploadedAt),
      invoiceDate: toTimestampOrNull(invoice.invoiceDate),
    }));
  }
  
  // Convert donation entries dates (pure allocation refs; strip legacy metadata)
  if (request.donationEntries) {
    docData.donationEntries = request.donationEntries.map(entry => {
      const { platformCost, actualReceived, url, donorName, linkTitle, ...cleanEntry } = entry as any;
      return {
        ...cleanEntry,
        addedAt: toTimestampOrNull(entry.addedAt),
      };
    });
  }
  
  // Convert updates dates
  if (request.updates) {
    docData.updates = request.updates.map(update => ({
      ...update,
      createdAt: toTimestampOrNull(update.createdAt),
    }));
  }
  
  // Remove undefined and null values at all depths - Firebase doesn't accept undefined
  const cleanedData = removeUndefined(docData);
  Object.keys(cleanedData).forEach(key => {
    if (cleanedData[key] === null) delete cleanedData[key];
  });
  
  await addDoc(requestsCollection, cleanedData);
  return caseNumber;
}

export async function updateTreatmentSupportRequest(id: string, data: Partial<TreatmentSupportRequest>) {
  const reqRef = doc(db, 'treatmentSupportRequests', id);
  
  const updateData: any = { ...data };
  
  // Convert all date fields safely
  if ('dateOfBirth' in data) updateData.dateOfBirth = toTimestampOrNull(data.dateOfBirth);
  if ('idExpiryDate' in data) updateData.idExpiryDate = toTimestampOrNull(data.idExpiryDate);
  if ('createdAt' in data) updateData.createdAt = toTimestampOrNull(data.createdAt);
  if ('updatedAt' in data) updateData.updatedAt = toTimestampOrNull(data.updatedAt);
  if ('reviewedAt' in data) updateData.reviewedAt = toTimestampOrNull(data.reviewedAt);
  if ('adminDecisionAt' in data) updateData.adminDecisionAt = toTimestampOrNull(data.adminDecisionAt);
  if ('invoicesUploadedAt' in data) updateData.invoicesUploadedAt = toTimestampOrNull(data.invoicesUploadedAt);
  if ('paymentSlipsUploadedAt' in data) updateData.paymentSlipsUploadedAt = toTimestampOrNull(data.paymentSlipsUploadedAt);
  if ('idDocumentUploadedAt' in data) updateData.idDocumentUploadedAt = toTimestampOrNull(data.idDocumentUploadedAt);
  if ('treatmentStartedAt' in data) updateData.treatmentStartedAt = toTimestampOrNull(data.treatmentStartedAt);

  // Convert medical reports dates
  if (data.medicalReports) {
    updateData.medicalReports = data.medicalReports.map(report => ({
      ...report,
      uploadedAt: toTimestampOrNull(report.uploadedAt),
      reportDate: toTimestampOrNull((report as any).reportDate),
    }));
  }

  // Convert progress reports dates
  if (data.progressReports) {
    updateData.progressReports = data.progressReports.map(report => ({
      ...report,
      uploadedAt: toTimestampOrNull(report.uploadedAt),
      reportDate: toTimestampOrNull(report.reportDate),
    }));
  }

  // Convert invoices dates
  if (data.invoices) {
    updateData.invoices = data.invoices.map(invoice => ({
      ...invoice,
      uploadedAt: toTimestampOrNull(invoice.uploadedAt),
      invoiceDate: toTimestampOrNull(invoice.invoiceDate),
    }));
  }
  
  // Convert donation entries dates. donationEntries are now pure allocation
  // refs; strip legacy/embedded link-metadata so stale copies never re-persist.
  if (data.donationEntries) {
    updateData.donationEntries = data.donationEntries.map(entry => {
      const { platformCost, actualReceived, url, donorName, linkTitle, ...cleanEntry } = entry as any;
      return {
        ...cleanEntry,
        addedAt: toTimestampOrNull(entry.addedAt),
      };
    });
  }
  
  // Convert updates dates
  if (data.updates) {
    updateData.updates = data.updates.map(update => ({
      ...update,
      createdAt: toTimestampOrNull(update.createdAt),
    }));
  }
  
  // Remove undefined values at all depths - Firebase doesn't accept undefined
  const cleanedData = removeUndefined(updateData);
  Object.keys(cleanedData).forEach(key => {
    if (cleanedData[key] === null) delete cleanedData[key];
  });
  
  await updateDoc(reqRef, cleanedData);
}

export async function deleteTreatmentSupportRequest(id: string) {
  const reqRef = doc(db, 'treatmentSupportRequests', id);
  await deleteDoc(reqRef);
}

export async function backfillCaseNumbers(): Promise<number> {
  const q = query(requestsCollection, orderBy('createdAt', 'asc'));
  const snapshot = await getDocs(q);
  let updated = 0;
  for (let i = 0; i < snapshot.docs.length; i++) {
    const docSnap = snapshot.docs[i];
    const data = docSnap.data();
    if (typeof data.caseNumber !== 'number') {
      await updateDoc(doc(db, 'treatmentSupportRequests', docSnap.id), { caseNumber: i + 1 });
      updated++;
    }
  }
  return updated;
}

/**
 * Throws if a link cannot be allocated to a case because of category mismatch.
 * Callers turn the thrown code into a localized toast.
 *
 * Error codes:
 *   - CATEGORY_MISSING: either side lacks a category
 *   - CATEGORY_MISMATCH: both sides set, values differ
 */
export function assertCategoryMatch(
  link: { treatmentCategoryId?: string },
  caseDoc: { categoryId?: string },
): void {
  if (!link.treatmentCategoryId || !caseDoc.categoryId) {
    const err = new Error('CATEGORY_MISSING');
    (err as any).code = 'CATEGORY_MISSING';
    throw err;
  }
  if (link.treatmentCategoryId !== caseDoc.categoryId) {
    const err = new Error('CATEGORY_MISMATCH');
    (err as any).code = 'CATEGORY_MISMATCH';
    throw err;
  }
}
